import type BABYLON from 'babylonjs';

import { createMeshesCustomShaderOptions, updateMeshesCustomShader } from './customShader';
import type { Globals } from './globals';
import { compactOptions } from './options';

const materialDefineName = 'MATERIAL_CHANNEL';

const [defines, fragment] = createMeshesCustomShaderOptions(materialDefineName, [
  {
    name: 'finalRender',
  },
  {
    name: 'baseColor',
    // cf. https://github.com/BabylonJS/Babylon.js/blob/5.48.1/packages/dev/core/src/Shaders/pbr.fragment.fx#L87-L89
    condition: 'ALBEDO',
    finalColor: 'vec4(albedoTexture.rgb,1)',
  },
  {
    name: 'roughness',
    // cf. https://github.com/BabylonJS/Babylon.js/blob/5.48.1/packages/dev/core/src/Shaders/pbr.fragment.fx#L209
    finalColor: 'vec4(roughness,roughness,roughness,1)',
  },
  {
    name: 'metallic',
    // cf. https://github.com/BabylonJS/Babylon.js/blob/5.48.1/packages/dev/core/src/Shaders/ShadersInclude/pbrFragmentDeclaration.fx#L9
    //     https://github.com/BabylonJS/Babylon.js/blob/5.48.1/packages/dev/core/src/Shaders/pbr.fragment.fx#L147-L156
    condition: 'defined(REFLECTIVITY)',
    beforeFragment: 'float metallic=vReflectivityColor.r*surfaceMetallicOrReflectivityColorMap.b;',
    finalColor: 'vec4(metallic,metallic,metallic,1)',
  },
  {
    name: 'transmission',
    // cf. https://github.com/BabylonJS/Babylon.js/blob/5.48.1/packages/dev/core/src/Shaders/ShadersInclude/pbrFragmentDeclaration.fx#L187
    //     https://github.com/BabylonJS/Babylon.js/blob/5.48.1/packages/dev/core/src/Shaders/pbr.fragment.fx#L505
    condition: 'SUBSURFACE&&SS_REFRACTIONINTENSITY_TEXTURE',
    beforeFragment:
      'float refractionIntensity=0.;refractionIntensity=vSubSurfaceIntensity.x*refractionIntensityMap.r;',
    finalColor: 'vec4(refractionIntensity,refractionIntensity,refractionIntensity,1)',
  },
]);

export const MATERIAL_CHANNELS = defines;

export interface MeshRenderingOptions {
  pbrMaterial?: {
    subSurface?: Partial<BABYLON.PBRSubSurfaceConfiguration>;
  };
  channel?: keyof typeof MATERIAL_CHANNELS | undefined;
}

export function updateMeshesRendering(
  globals: Globals,
  meshes: BABYLON.AbstractMesh[],
  options?: MeshRenderingOptions,
  markAsDirty = false
): void {
  if (options) {
    meshes.forEach((mesh) => {
      const { pbrMaterial, channel } = options;
      if (pbrMaterial && mesh.material instanceof globals.BABYLON.PBRMaterial) {
        if (pbrMaterial.subSurface)
          Object.assign(mesh.material.subSurface, compactOptions(pbrMaterial.subSurface));
      }
      if (!channel) {
        updateMeshesCustomShader(globals, meshes, {
          materialDefines: {
            [materialDefineName]: MATERIAL_CHANNELS.finalRender,
          },
        });
      } else {
        updateMeshesCustomShader(globals, meshes, {
          fragmentBeforeFragColor: fragment,
          materialDefines: {
            [materialDefineName]: MATERIAL_CHANNELS[channel],
          },
        });
      }
      if (markAsDirty && mesh.material instanceof globals.BABYLON.PBRMaterial)
        mesh.material.markAsDirty(globals.BABYLON.Material.MiscDirtyFlag);
    });
  }
}
